import type { RenderContext, VideoEffect, VideoFrame } from '../types';
import { evaluateEffectParameters } from '../keyframes';
import { drawFullscreenQuad, WebGLShaderProgram } from '../gpu/shader';
import { uploadCanvasToTexture } from '../gpu/texturePool';
import { VERTEX_FULLSCREEN, FRAG_COLOR_GRADE } from '../shaders/glsl';

export abstract class BaseEffect implements VideoEffect {
  id: string;
  type: string;
  startTime: number;
  endTime: number;
  enabled: boolean;
  parameters: import('../types').EffectParameters;
  keyframeTracks?: Record<string, import('../types').EffectKeyframe[]>;

  constructor(
    id: string,
    type: string,
    clip: Partial<import('../types').EffectTimelineClip>,
    private readonly defaults: Record<string, number>,
  ) {
    this.id = id;
    this.type = type;
    this.startTime = clip.startTime ?? 0;
    this.endTime = clip.endTime ?? 60;
    this.enabled = clip.enabled !== false;
    this.parameters = clip.parameters ?? {};
    this.keyframeTracks = clip.keyframeTracks;
  }

  protected resolvedParams(context: RenderContext): Record<string, number> {
    const local = Math.max(0, context.currentTime - context.effectStartTime);
    return evaluateEffectParameters(
      this.parameters,
      this.keyframeTracks,
      local,
      this.defaults,
    );
  }

  render(frame: VideoFrame, context: RenderContext): VideoFrame {
    if (!this.enabled) return frame;
    const params = this.resolvedParams(context);
    if (context.gpu) {
      try {
        return this.renderGpu(frame, params, context);
      } catch {
        return this.renderCpu(frame, params, context);
      }
    }
    return this.renderCpu(frame, params, context);
  }

  protected abstract renderGpu(
    frame: VideoFrame,
    params: Record<string, number>,
    context: RenderContext,
  ): VideoFrame;

  protected abstract renderCpu(
    frame: VideoFrame,
    params: Record<string, number>,
    context: RenderContext,
  ): VideoFrame;

  protected runShaderPass(
    frame: VideoFrame,
    program: WebGLShaderProgram,
    setup: (gl: WebGL2RenderingContext, program: WebGLShaderProgram) => void,
    context: RenderContext,
  ): VideoFrame {
    const gpu = context.gpu!;
    const gl = gpu.gl;
    const pool = gpu.texturePool;
    const srcTex = pool.acquire(frame.width, frame.height);
    const dstTex = pool.acquire(frame.width, frame.height);
    uploadCanvasToTexture(gl, srcTex, frame.canvas);
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, dstTex, 0);
    gl.viewport(0, 0, frame.width, frame.height);
    program.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    setup(gl, program);
    drawFullscreenQuad(gl, program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
    pool.release(srcTex);
    const outCanvas = document.createElement('canvas');
    outCanvas.width = frame.width;
    outCanvas.height = frame.height;
    // Read back via blit to canvas (simplified: use 2d draw from WebGL canvas)
    const readFb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, readFb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, dstTex, 0);
    const pixels = new Uint8Array(frame.width * frame.height * 4);
    gl.readPixels(0, 0, frame.width, frame.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(readFb);
    const ctx2d = outCanvas.getContext('2d');
    if (ctx2d) {
      const img = ctx2d.createImageData(frame.width, frame.height);
      for (let y = 0; y < frame.height; y += 1) {
        const srcRow = (frame.height - 1 - y) * frame.width * 4;
        img.data.set(pixels.subarray(srcRow, srcRow + frame.width * 4), y * frame.width * 4);
      }
      ctx2d.putImageData(img, 0, 0);
    }
    pool.release(dstTex);
    return { width: frame.width, height: frame.height, canvas: outCanvas, texture: null };
  }
}

let colorGradeProgram: WebGLShaderProgram | null = null;

export function getColorGradeProgram(gl: WebGL2RenderingContext): WebGLShaderProgram {
  if (!colorGradeProgram) {
    colorGradeProgram = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_COLOR_GRADE);
  }
  return colorGradeProgram;
}
